import * as THREE from 'three';

export interface BuildingCollider {
  position: [number, number, number];
  size: [number, number, number]; // width, height, depth
}

/**
 * CollisionDetection — AABB collision for player vs objects.
 * Returns a resolved position (pushed out of collision) and whether a collision occurred.
 */
export function resolveCollisions(
  playerPos: THREE.Vector3,
  playerRadius: number,
  colliders: BuildingCollider[],
): { position: THREE.Vector3; collided: boolean } {
  const result = playerPos.clone();
  let collided = false;

  for (const obj of colliders) {
    const [bx, by, bz] = obj.position;
    const [bw, bh, bd] = obj.size;

    // AABB bounds
    const minX = bx - bw / 2 - playerRadius;
    const maxX = bx + bw / 2 + playerRadius;
    const minZ = bz - bd / 2 - playerRadius;
    const maxZ = bz + bd / 2 + playerRadius;

    // Check if player is inside the expanded AABB
    if (
      result.x > minX &&
      result.x < maxX &&
      result.z > minZ &&
      result.z < maxZ
    ) {
      collided = true;

      // Push player out along the shortest axis
      const overlapX = Math.min(Math.abs(result.x - minX), Math.abs(result.x - maxX));
      const overlapZ = Math.min(Math.abs(result.z - minZ), Math.abs(result.z - maxZ));

      if (overlapX < overlapZ) {
        result.x = result.x < bx ? minX : maxX;
      } else {
        result.z = result.z < bz ? minZ : maxZ;
      }
    }
  }

  return { position: result, collided };
}

/**
 * Build collider list from positions with per-object sizes.
 * Falls back to a sensible default for stages if no size given.
 */
export function createBuildingColliders(
  objects: Array<{ position: [number, number, number]; size?: [number, number, number] }>,
  defaultSize: [number, number, number] = [7, 5, 5],
): BuildingCollider[] {
  return objects.map((obj) => ({
    position: obj.position,
    size: obj.size ?? defaultSize,
  }));
}
